import {
  Component,
  ChangeDetectionStrategy,
  inject,
  viewChild
} from '@angular/core';
import { ZefReactiveComponent } from '@zerops/zef/core';
import { Store, select } from '@ngrx/store';
import {
  Subject,
  map,
  withLatestFrom,
  filter,
  switchMap
} from 'rxjs';
import { formValueOnValid } from '@zerops/zef/forms';
import { zefDialogClose, selectZefDialogState } from '@zerops/zef/dialog';
import { selectZefProgressByType } from '@zerops/zef/progress';
import { FEATURE_NAME } from './l7-config-dialog.constant';
import { L7ConfigForm } from './l7-config-dialog.form';
import {
  updateL7HttpBalancerConfig,
  loadL7HttpBalancerConfig,
  selectProjectL7Config
} from '@zerops/zerops/core/project-base';
import { L7ConfigFormComponent } from './components/l7-config-form.component';
import { transformL7FormDataToPayload } from './l7-config.dialog.utils';

@Component({
  selector: 'z-l7-config-dialog',
  templateUrl: './l7-config-dialog.feature.html',
  styleUrl: './l7-config-dialog.feature.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class L7ConfigDialogFeature extends ZefReactiveComponent {
  // # Deps
  #store = inject(Store);
  #form = inject(L7ConfigForm);
  formRef = viewChild(L7ConfigFormComponent);

  // # Event Streams
  onClose$ = new Subject<void>();
  onSubmit$ = new Subject<void>();
  onResetToDefaults$ = new Subject<void>();

  // # Data
  // -- sync
  key = FEATURE_NAME;
  actionKey = updateL7HttpBalancerConfig.type;

  // -- async
  dialogState$ = this.#store.pipe(select(selectZefDialogState(FEATURE_NAME)));
  open$ = this.dialogState$.pipe(map((data) => data?.state));
  projectId$ = this.dialogState$.pipe(map((data) => data?.meta?.projectId));
  formState$ = this.#form.state$;
  configLoading$ = this.#store.pipe(
    select(selectZefProgressByType(loadL7HttpBalancerConfig.type)),
    map((progress) => !!progress)
  );
  configData$ = this.projectId$.pipe(
    filter((id) => !!id),
    switchMap((projectId) => this.#store.pipe(select(selectProjectL7Config(projectId))))
  );

  // # State resolver
  state = this.$connect({
    open: this.open$,
    projectId: this.projectId$,
    formState: this.formState$,
    configLoading: this.configLoading$,
    configData: this.configData$
  });

  constructor() {
    super();

    // # Dispatcher
    this.$dispatchActions([

      this.onClose$.pipe(
        map(() => zefDialogClose({ key: FEATURE_NAME }))
      ),

      this.onSubmit$.pipe(
        formValueOnValid(this.#form),
        withLatestFrom(this.projectId$),
        map(([ formValue, projectId ]) => updateL7HttpBalancerConfig({
          payload: transformL7FormDataToPayload(formValue),
          projectId
        }))
      )

    ]);
  }
}
